'use client';

import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

import {
  getAdsIdsFromUrl,
  getIpData,
  getSystemData,
  getTimezoneName,
} from '@monoimin/utils/analytics';
import { getCookie } from '@monoimin/utils/helpers';

import { Locales } from '@monoimin/sb-types';

import { createCountryInfo } from '../../utils/createCountryInfo';
import { trackAmplitudeEvent } from '../../amplitude/amplitude';
import { getDeviceInfo } from '../../amplitude';

export type TrackPageViewProps = {
  data: any;
  locale: Locales;
  isRichLayout: boolean;
  pageLevel: number;
  authorName?: string;
  expertName?: string;
  programName?: string | string[] | null;
  pageInitiator?: string;
};

const normalizeValue = (value?: string | string[] | null) => {
  if (value === null || value === undefined) {
    return null;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return null;
    }
    if (value.length === 1) {
      return value[0];
    }
    return JSON.stringify(value);
  }
  return value;
};

export function TrackPageViewClient({
  data,
  locale,
  isRichLayout,
  pageLevel,
  authorName = '',
  expertName = '',
  programName = null,
  pageInitiator = '',
}: TrackPageViewProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const sendPageViewData = async () => {
      window.dataLayer = window.dataLayer || [];

      const ipData = await getIpData();
      const device_country_ip = ipData?.ipcountry || '';
      const device_country_code_ip = ipData?.ipCountryCode || '';
      const device_city_ip = ipData?.ipcity || '';
      const device_region = ipData?.ipCountryRegion || '';
      const device_ip = ipData?.fr_ip || '';

      // Get all the analytics data
      const site_hostname = window.location.hostname || '';
      const site_domain = window.location.hostname
        ? window.location.hostname.split('.').slice(-2).join('.')
        : '';
      const page_path = window.location.pathname || '';
      const page_location =
        (window.location.origin || '') + (window.location.pathname || '');
      const page_hash = window.location.hash || '';
      const page_url = window.location.href || '';

      const adsIds = getAdsIdsFromUrl();

      const site_gclid = adsIds['gclid'] || '';
      const site_fbclid = adsIds['fbclid'] || '';
      const site_wbraid = adsIds['wbraid'] || '';
      const site_ttclid = adsIds['ttclid'] || '';
      const site_rdt_cid = adsIds['rdt_cid'] || '';
      const site_li_fat = adsIds['initial_twclid'] || '';
      const site_twclid = adsIds['twclid'] || '';
      const site_msclkid = adsIds['msclkid'] || '';
      const site_dclid = adsIds['dclid'] || '';
      const site_utm_content = adsIds['utm_content'] || '';
      const site_utm_campaign = adsIds['utm_campaign'] || '';
      const site_utm_term = adsIds['utm_term'] || '';
      const site_utm_medium = adsIds['utm_medium'] || '';
      const site_utm_source = adsIds['utm_source'] || '';

      // More browser/device info
      const device_browser_language = (navigator.language || '').toLowerCase();
      const device_referrer_domain =
        document.referrer && new URL(document.referrer).hostname;
      const device_referrer_url = document.referrer || '';
      const device_cookie_consent = getCookie('cookie_consent') || '';
      const device_screen_resolution = `${window.screen.width}x${window.screen.height}`;

      const deviceInfo = await getDeviceInfo();

      // System info
      const userSystemData = getSystemData();
      const device_os_version = userSystemData.device_os_version;
      const device_browser_name = userSystemData.device_browser_name;
      const device_platform = userSystemData.device_os_name;
      const device_browser_version = userSystemData.device_browser_version;
      const device_type = userSystemData.device_type;
      const device_marketing_name = userSystemData.device_marketing_name;
      const device_model = userSystemData.device_model;
      const device_vendor = userSystemData.device_vendor;
      const device_latitude = getCookie('lat') || '';
      const device_longitude = getCookie('lon') || '';

      // Content info
      const { country: pageCountry, region: programRegion } =
        await createCountryInfo(data?.content);

      const program_region = normalizeValue(programRegion);
      const program_country = normalizeValue(pageCountry);
      const program_name = normalizeValue(programName);

      const page_initiator = pageInitiator;
      const page_author_name = authorName;
      const page_expert_name = expertName;
      const page_title =
        data?.page_title ??
        data?.content?.pageTitle ??
        data?.name ??
        data?.content?.seo?.[0]?.title ??
        data?.content?.title;
      const page_new_window_flag = window.opener !== null;

      const program_flag = !!program_name;

      const page_header_flag = isRichLayout;
      const page_footer_flag = isRichLayout;
      const page_level = pageLevel;

      const page_form_count =
        data?.page_form_count ??
        (data?.content?.formId || data?.content?.component === 'mtgPage'
          ? 1
          : 0);

      const page_block_flag = data?.content?.body?.length > 0;
      const page_language = locale;
      const event_type = 'view';
      const event_datetime = new Date().toISOString();
      const device_timezone = getTimezoneName();

      // Prepare the data for DataLayer
      const pageViewData = {
        event: 'page_view',
        device_country_ip,
        device_country_code_ip,
        device_city_ip,
        device_region,
        device_ip,
        site_hostname,
        site_domain,
        page_hash,
        page_path,
        page_location,
        page_url,
        site_gclid,
        site_fbclid,
        site_wbraid,
        site_ttclid,
        site_rdt_cid,
        site_li_fat,
        site_twclid,
        site_msclkid,
        site_dclid,
        site_utm_content,
        site_utm_campaign,
        site_utm_term,
        site_utm_medium,
        site_utm_source,
        device_browser_language,
        device_referrer_domain,
        device_referrer_url,
        device_cookie_consent,
        device_screen_resolution,
        device_os_version,
        device_browser_name,
        device_platform,
        device_browser_version,
        device_type,
        device_marketing_name,
        device_model,
        device_vendor,
        device_latitude,
        device_longitude,
        program_region,
        program_country,
        page_initiator,
        page_author_name,
        page_expert_name,
        page_title,
        page_new_window_flag,
        program_name,
        program_flag,
        page_header_flag,
        page_footer_flag,
        page_level,
        page_form_count,
        page_block_flag,
        page_language,
        event_type,
        event_datetime,
        device_timezone,
      };

      // Push to dataLayer
      if (window.dataLayer) {
        window.dataLayer.push(pageViewData);
      }

      await trackAmplitudeEvent(
        {
          deviceId: deviceInfo.deviceId,
          userId: null,
          name: 'Page View',
          properties: {
            ...pageViewData,
            page_language: locale,
            page_path: pathname,
            page_url: window.location.href,
            page_title,
            page_type: data?.content?.component || 'page',
          },
        },
        false
      );
    };

    sendPageViewData();
  }, [pathname, searchParams]);

  // This component doesn't render anything
  return null;
}
